import { formatCopy } from "../../theme/copy";
import { Fragment } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { Box, Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionArrangement } from "../contracts";
import type { GridQuestionGroupDefinition } from "./core";

export type GridQuestionGroupItem = {
  childId: string;
  row: string;
  field: number;
  complete?: boolean;
  content: ReactNode;
};

export const GridQuestionGroupLayout = ({
  definition,
  arrangement,
  items,
}: {
  definition: GridQuestionGroupDefinition;
  arrangement: QuestionArrangement;
  items: readonly GridQuestionGroupItem[];
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const border = `1px solid ${theme.tokens.color.border.default}`;
  const wide = arrangement === "wide";

  return (
    <Box
      data-testid={
        wide ? "grid-question-group-table" : "grid-question-group-cards"
      }
      data-grid-layout="responsive"
      sx={{ minWidth: 0, width: "100%" }}
    >
      {definition.rows.map((row) => {
        const rowItems = definition.fields.flatMap((field) => {
          const item = items.find(
            (candidate) =>
              candidate.row === row.value && candidate.field === field,
          );
          return item ? [item] : [];
        });
        const completed = rowItems.filter((item) => item.complete).length;
        const pending = rowItems.length - completed;

        return (
          <Fragment key={row.value}>
            <VisualGroupHeading
              text={groupHeading(definition.rowGroups, row.value)}
            />
            <Box
              component="section"
              data-testid={`grid-question-group-row-${row.value}`}
              aria-label={row.text || row.value}
              sx={{
                borderBottom: border,
                minWidth: 0,
                py: `${theme.tokens.spacing["5"]}px`,
                "&:first-of-type": { pt: 0 },
              }}
            >
              <Box
                sx={{
                  alignItems: "baseline",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: `${theme.tokens.spacing["2"]}px`,
                  justifyContent: row.display_text
                    ? "space-between"
                    : "flex-end",
                  mb: `${theme.tokens.spacing["3"]}px`,
                }}
              >
                {row.display_text && (
                  <Typography
                    component="h2"
                    sx={{
                      color: theme.tokens.color.text.primary,
                      fontSize: theme.tokens.typography.size.xl,
                      fontWeight: theme.tokens.typography.weight.bold,
                    }}
                  >
                    {row.text}
                  </Typography>
                )}
                <Typography
                  role="status"
                  aria-label={formatCopy(theme.copy.status.groupProgress, {
                    completed,
                    total: rowItems.length,
                    pending,
                  })}
                  sx={{
                    color: theme.tokens.color.text.secondary,
                    fontSize: theme.tokens.typography.size.sm,
                  }}
                >
                  {formatCopy(theme.copy.status.groupProgressVisible, {
                    completed,
                    total: rowItems.length,
                    pending,
                  })}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gap: `${theme.tokens.spacing["5"]}px`,
                  gridTemplateColumns: wide
                    ? "repeat(auto-fit, minmax(min(100%, 220px), 1fr))"
                    : "minmax(0, 1fr)",
                  minWidth: 0,
                }}
              >
                {rowItems.map((item) => (
                  <Box
                    key={item.childId}
                    data-grid-field={item.field}
                    sx={{ minWidth: 0 }}
                  >
                    {item.content}
                  </Box>
                ))}
              </Box>
            </Box>
          </Fragment>
        );
      })}
    </Box>
  );
};
